import { COPY } from '@/shared/constants/copies';

export type MediaPermissionType = 'camera' | 'microphone';
export type MediaPermissionStatus = 'granted' | 'denied' | 'prompt' | 'unknown';

// Common video MIME types in order of preference
const VIDEO_MIME_TYPES = [
	'video/webm;codecs=vp9,opus',
	'video/webm;codecs=vp8,opus',
	'video/webm',
	'video/mp4',
] as const;

/**
 * Check browser permission state for camera/microphone
 */
export async function checkMediaPermissions(
	types: MediaPermissionType[]
): Promise<MediaPermissionStatus> {
	try {
		if (!navigator.permissions?.query) {
			return 'unknown';
		}

		const permissionChecks = types.map(async (type) => {
			const result = await navigator.permissions.query({
				name: type as PermissionName,
			});
			return result.state;
		});

		const results = await Promise.all(permissionChecks);

		// If any permission is denied, return denied
		if (results.some((state) => state === 'denied')) {
			return 'denied';
		}

		// If all are granted, return granted
		if (results.every((state) => state === 'granted')) {
			return 'granted';
		}

		// Otherwise, prompt is needed
		return 'prompt';
	} catch {
		return 'unknown';
	}
}

export type MediaStreamError = {
	type:
		| 'NotAllowedError'
		| 'NotFoundError'
		| 'NotReadableError'
		| 'OverconstrainedError'
		| 'UnknownError';
	message: string;
};

/**
 * Get camera/microphone stream with standardized error handling
 */
export async function getMediaStream(
	constraints: MediaStreamConstraints
): Promise<{ stream: MediaStream } | { error: MediaStreamError }> {
	try {
		const stream = await navigator.mediaDevices.getUserMedia(constraints);
		return { stream };
	} catch (err) {
		const errorName = err instanceof Error ? err.name : 'UnknownError';

		const errorMessages: Record<string, string> = {
			NotAllowedError: COPY.fields.mediaDenied,
			NotFoundError: COPY.fields.mediaNotFound,
			NotReadableError: COPY.fields.mediaInUse,
			OverconstrainedError: COPY.fields.mediaUnsupported,
		};

		return {
			error: {
				type: errorName as MediaStreamError['type'],
				message: errorMessages[errorName] || 'Unable to access camera and microphone.',
			},
		};
	}
}

/**
 * Get browser-supported video MIME type
 */
export function getSupportedVideoMimeType(): string {
	for (const mimeType of VIDEO_MIME_TYPES) {
		if (MediaRecorder.isTypeSupported(mimeType)) {
			return mimeType;
		}
	}
	return '';
}

/**
 * Safely stop all tracks in a media stream
 */
export function stopMediaStream(stream: MediaStream | null): void {
	if (stream) {
		stream.getTracks().forEach((track) => track.stop());
	}
}
